import { IconCheck, IconCloudUpload, IconUpload } from "@tabler/icons-react";
import { useRef, useState } from "react";
import { Loader, Stack, Text, ThemeIcon } from "@/components/ui";

export type CategoriaDropzoneEstado = "cargado" | "vacio-requerido" | "vacio-opcional";

interface Props {
  accept: string;
  /** Tamaño máximo por archivo en MB (ajuste del panel de administración). */
  maxMb?: number;
  cargando: boolean;
  estado: CategoriaDropzoneEstado;
  onFiles: (files: FileList) => void;
  /** Single-file: solo permite 1 archivo (input sin `multiple`) y copy en singular. */
  single?: boolean;
}

/**
 * Dropzone multi-archivo específico de las categorías de documentos.
 *
 * Tres estados visuales:
 * - `cargado`: verde · "ya hay archivos · puedes agregar más"
 * - `vacio-requerido`: ámbar · animado · sobresale para indicar acción pendiente
 * - `vacio-opcional`: neutral · estilo base
 *
 * Difiere del `FileDropzone` compartido en que: (a) acepta múltiples
 * archivos en un solo drop / pick, (b) tiene styling más compacto
 * porque vive embebido en una card más pequeña por categoría.
 */
export function CategoriaDropzone({ accept, maxMb = 50, cargando, estado, onFiles, single = false }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const visual = getVisualPorEstado(estado, dragActive, accept, single);

  return (
    <>
      {/* Animación de pulso para llamar la atención cuando es requerido vacío. */}
      <style>{PULSE_KEYFRAMES}</style>
      <div
        onDragEnter={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          onFiles(e.dataTransfer.files);
        }}
        onClick={() => !cargando && inputRef.current?.click()}
        className="rounded-lg text-center transition-all duration-160"
        style={{
          padding: estado === "vacio-requerido" ? 28 : 20,
          border: `${visual.borderWidth}px ${visual.borderStyle} ${visual.borderColor}`,
          background: visual.background,
          cursor: cargando ? "default" : "pointer",
          animation: visual.animation,
        }}
      >
        <Stack align="center" gap={estado === "vacio-requerido" ? 10 : 6}>
          {cargando ? (
            <Loader size="xs" />
          ) : (
            <ThemeIcon size={estado === "vacio-requerido" ? "lg" : "md"} variant={visual.iconVariant} color={visual.iconColor}>
              {visual.icon}
            </ThemeIcon>
          )}
          <Text size={estado === "vacio-requerido" ? "md" : "sm"} fw={600} c={visual.textColor}>
            {cargando ? "Subiendo…" : visual.titulo}
          </Text>
          <Text size="xs" c="dimmed">
            {visual.subtitulo ?? `${accept} · máx ${maxMb} MB por archivo`}
          </Text>
        </Stack>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          multiple={!single}
          accept={accept}
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) onFiles(e.target.files);
            // Limpiar el value · sin esto, re-seleccionar el MISMO archivo (p. ej.
            // tras borrarlo) no dispara onChange (el navegador ve el mismo value) y
            // el archivo "no se carga". Resetear permite volver a subir el mismo file.
            e.target.value = "";
          }}
        />
      </div>
    </>
  );
}

interface VisualEstado {
  borderWidth: number;
  borderStyle: "dashed" | "solid";
  borderColor: string;
  background: string;
  iconVariant: "light" | "filled";
  iconColor: string;
  icon: React.ReactNode;
  titulo: string;
  subtitulo?: string;
  textColor?: string;
  animation?: string;
}

function getVisualPorEstado(estado: CategoriaDropzoneEstado, dragActive: boolean, accept: string, single: boolean): VisualEstado {
  if (dragActive) {
    return {
      borderWidth: 2,
      borderStyle: "dashed",
      borderColor: "var(--color-multiva-500)",
      background: "color-mix(in srgb, var(--color-multiva-600) 8%, transparent)",
      iconVariant: "light",
      iconColor: "multiva",
      icon: <IconUpload size={16} stroke={1.7} />,
      titulo: single ? "Suelta el archivo aquí" : "Suelta los archivos aquí",
      subtitulo: single ? "Un solo archivo" : "Puedes soltar varios a la vez",
    };
  }

  if (estado === "cargado") {
    return {
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: "var(--color-multiva-300)",
      background: "color-mix(in srgb, var(--color-multiva-600) 6%, transparent)",
      iconVariant: "filled",
      iconColor: "multiva",
      icon: <IconCheck size={18} stroke={2.4} />,
      titulo: "Archivos cargados",
      subtitulo: `Puedes agregar más · uno o varios a la vez · ${accept.replaceAll(",", ", ")}`,
      textColor: "multiva-800",
    };
  }

  if (estado === "vacio-requerido") {
    return {
      borderWidth: 2,
      borderStyle: "dashed",
      borderColor: "var(--color-advertencia-300)",
      background: "color-mix(in srgb, var(--color-advertencia-600) 8%, transparent)",
      iconVariant: "light",
      iconColor: "advertencia",
      icon: <IconCloudUpload size={22} stroke={2} />,
      titulo: single ? "Arrastra un archivo PDF aquí o haz clic" : "Arrastra uno o más archivos aquí o haz clic",
      subtitulo: single ? `Requerido para continuar · un solo archivo (${accept})` : "Requerido para continuar · puedes subir varios a la vez",
      textColor: "advertencia-800",
      animation: "multiva-dropzone-pulse 2.4s ease-in-out infinite",
    };
  }

  return {
    borderWidth: 2,
    borderStyle: "dashed",
    borderColor: "var(--color-border)",
    // Vacío: gris claro relativo al texto (mismo tono que los slots de carga del CFDI).
    background: "color-mix(in srgb, var(--color-foreground) 5%, transparent)",
    iconVariant: "light",
    iconColor: "multiva",
    icon: <IconUpload size={16} stroke={1.7} />,
    titulo: single ? "Arrastra un archivo o haz clic" : "Arrastra uno o más archivos o haz clic",
  };
}

const PULSE_KEYFRAMES = `
  @keyframes multiva-dropzone-pulse {
    0%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-advertencia-600) 10%, transparent); }
    50%      { box-shadow: 0 0 0 5px color-mix(in srgb, var(--color-advertencia-600) 0%, transparent); }
  }
`;
